import React from 'react';

import { isFunction, isNumber } from 'utils/check';
import { useWindowHasChanges } from 'ui/hooks';
import { getCSSProperties } from 'ui/utils';

import classes from './AnchorPopup.module.scss';
import type { AnchorPopupProps } from './types';
import { useAnchorPopupView } from './useAnchorPopupView';

import { autoUpdate, flip, offset, shift, size, useFloating } from '@floating-ui/react';

const AnchorPopupInternal: React.ForwardRefRenderFunction<
  HTMLDivElement,
  AnchorPopupProps
> = (props, ref) => {
  const {
    children,
    anchorRef,
    enabled,
    minWidth: externalMinWidth,
    height = 250,
    placement = 'bottom-start',
  } = props;
  const { rootProps } = useAnchorPopupView(props, classes);
  const popupRootRef = React.useRef<HTMLDivElement | null>(null);
  const resolvedRef = isFunction(ref)
    ? popupRootRef
    : (ref as React.RefObject<HTMLDivElement>);

  const [popupMinWidth, setPopupMinWidth] = React.useState<number>(200);

  const { sizeChanges, scrollChanges } = useWindowHasChanges(enabled);

  const { x, y, refs, strategy, update } = useFloating({
    placement,
    open: enabled,
    middleware: [
      offset(4),
      flip(),
      shift({ padding: 8 }),
      size({
        apply({ availableHeight, rects, elements }) {
          const anchorWidth = rects.reference.width;
          const finalMinWidth = isNumber(externalMinWidth)
            ? externalMinWidth
            : Math.max(anchorWidth, popupMinWidth);

          Object.assign(elements.floating.style, {
            minWidth: `${finalMinWidth}px`,
            maxHeight: `${Math.min(height, availableHeight)}px`,
          });
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  React.useEffect(() => {
    if (anchorRef.current) refs.setReference(anchorRef.current);
    if (popupRootRef.current) refs.setFloating(popupRootRef.current);
  }, [anchorRef, refs]);

  React.useEffect(() => {
    if (!enabled || !popupRootRef.current) return;

    const css = getCSSProperties(popupRootRef.current);

    setPopupMinWidth(externalMinWidth || css.getPropFloatValue?.('min-width') || 200);

    update?.();
  }, [enabled, update, height, externalMinWidth, sizeChanges, scrollChanges]);

  return (
    <div
      ref={(node) => {
        popupRootRef.current = node;
        if (resolvedRef && typeof resolvedRef !== 'function') {
          resolvedRef.current = node;
        }
      }}
      className={rootProps.className}
      style={{
        position: strategy,
        top: y ?? 0,
        left: x ?? 0,
        opacity: enabled ? 1 : 0,
        zIndex: 999,
      }}
    >
      {children}
    </div>
  );
};

export const AnchorPopup = React.memo(React.forwardRef(AnchorPopupInternal));
